/**
 * Content extraction and preservation for section variant swaps.
 * Extracts text/image content from a section's HTML by cs-* class selectors,
 * then applies it to a new section's matching selectors.
 *
 * Navigation and footer sections use semantic selectors to avoid cross-role
 * content scrambling. Other categories use the generic a[href] / img catch-all.
 */

// Generic content selectors — used for all non-navigation categories.
const CONTENT_SELECTORS = [
  { selector: '.cs-topper', type: 'text' },
  { selector: '.cs-title', type: 'text' },
  { selector: '.cs-text', type: 'text' },
  { selector: '.cs-h3', type: 'text' },
  { selector: '.cs-item-p', type: 'text' },
  { selector: '.cs-item-text', type: 'text' },
  { selector: '.cs-name', type: 'text' },
  { selector: '.cs-job', type: 'text' },
  { selector: '.cs-button-solid', type: 'text' },
  { selector: 'img', type: 'image' },
  { selector: 'a[href]', type: 'link' }
];

// Navigation-specific selectors — semantic matching by link role.
// Prevents logo ↔ menu link ↔ social link scrambling.
const NAV_CONTENT_SELECTORS = [
  // Text
  { selector: '.cs-topper', type: 'text' },
  { selector: '.cs-title', type: 'text' },
  { selector: '.cs-text', type: 'text' },
  { selector: '.cs-h3', type: 'text' },
  { selector: '.cs-item-p', type: 'text' },
  { selector: '.cs-item-text', type: 'text' },
  { selector: '.cs-name', type: 'text' },
  { selector: '.cs-job', type: 'text' },
  { selector: '.cs-button-solid', type: 'text' },
  // Dropdown toggle labels (span/button.cs-li-link, not anchors)
  { selector: '.cs-li-link:not(a):not(.cs-drop-link)', type: 'text' },
  // Semantic nav images (specific before catch-all)
  { selector: '.cs-logo img', type: 'image' },
  // Semantic nav links
  { selector: 'a.cs-li-link:not(.cs-drop-link)', type: 'link' },
  { selector: 'a.cs-li-link.cs-drop-link', type: 'link' },
  { selector: 'a.cs-button-solid', type: 'link' }
  // No catch-all image or a[href] in nav mode — phone/social/decorative icons
  // should keep target-variant defaults to avoid cross-role icon scrambling.
];

// Footer-specific selectors — semantic matching by footer role.
// Prevents contact links from being remapped into unrelated legal/social/nav links.
const FOOTER_CONTENT_SELECTORS = [
  // Text
  { selector: '.cs-topper', type: 'text' },
  { selector: '.cs-title', type: 'text' },
  { selector: '.cs-text', type: 'text' },
  { selector: '.cs-h3', type: 'text' },
  { selector: '.cs-item-p', type: 'text' },
  { selector: '.cs-item-text', type: 'text' },
  { selector: '.cs-name', type: 'text' },
  { selector: '.cs-job', type: 'text' },
  { selector: '.cs-button-solid', type: 'text' },
  { selector: '.cs-copyright', type: 'text' },
  // Images (do not include generic img in footers)
  { selector: '.cs-logo img', type: 'image' },
  // Semantic footer links by role/class
  { selector: 'a.cs-logo', type: 'link' },
  { selector: 'a.cs-nav-link', type: 'link' },
  { selector: 'a.cs-link', type: 'link' },
  { selector: 'a.cs-contact-link', type: 'link' },
  { selector: 'a.cs-copyright-link', type: 'link' },
  { selector: 'a.cs-button-solid', type: 'link' },
  { selector: 'a.cs-social-link', type: 'link' }
];

// Role-specific dispatch — literal compares are intentional here. SHARED_SECTION_CATEGORIES
// is the exhaustive list; if it ever grows, both branches below need an update.
function getSelectors(category) {
  if (category === 'navigation') return NAV_CONTENT_SELECTORS;
  if (category === 'footer') return FOOTER_CONTENT_SELECTORS;
  return CONTENT_SELECTORS;
}

/**
 * Extract editable content from section HTML.
 * Returns an array of { selector, type, index, content } objects.
 *
 * For text: content is innerHTML.
 * For images: content is { src, alt, srcset? }.
 * For links: content is { href, text }.
 *
 * @param {string} html
 * @param {string} [category] - Section category (e.g. 'navigation'). Controls selector list.
 */
export function extractContent(html, category) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  const extracted = [];
  const imageMatched = new Set();
  const linkMatched = new Set();
  const selectors = getSelectors(category);

  for (const { selector, type } of selectors) {
    const elements = root.querySelectorAll(selector);
    let selectorIndex = 0;
    elements.forEach((el) => {
      // Per-type dedup: skip elements already matched by a more specific selector
      if (type === 'image' && imageMatched.has(el)) return;
      if (type === 'link' && linkMatched.has(el)) return;

      if (type === 'text') {
        extracted.push({
          selector,
          type,
          index: selectorIndex++,
          content: el.innerHTML
        });
      } else if (type === 'image') {
        if (el.classList.contains('pb-broken-img')) return;
        imageMatched.add(el);
        extracted.push({
          selector,
          type,
          index: selectorIndex++,
          content: {
            src: el.getAttribute('src') || '',
            alt: el.getAttribute('alt') || '',
            srcset: el.getAttribute('srcset') || null
          }
        });
      } else if (type === 'link') {
        linkMatched.add(el);
        extracted.push({
          selector,
          type,
          index: selectorIndex++,
          content: {
            href: el.getAttribute('href') || '',
            text: el.textContent
          }
        });
      }
    });
  }

  return extracted;
}

/**
 * Apply extracted content to a new section's HTML.
 * Matches by selector + index (first .cs-topper → first .cs-topper, etc).
 *
 * Returns { html, mismatches } where mismatches counts fields that couldn't
 * be mapped (old section had more elements than new section for a selector).
 *
 * @param {string} html
 * @param {Array} extracted
 * @param {string} [category] - Section category. Controls selector list.
 */
export function applyContent(html, extracted, category) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  let mismatches = 0;
  const imageMatched = new Set();
  const linkMatched = new Set();
  const selectors = getSelectors(category);

  // Pre-build target element lists with per-type dedup (mirrors extraction order)
  const targetMap = new Map(); // selector → [element, ...]
  for (const { selector, type } of selectors) {
    const elements = root.querySelectorAll(selector);
    const deduped = [];
    elements.forEach((el) => {
      if (type === 'image' && imageMatched.has(el)) return;
      if (type === 'link' && linkMatched.has(el)) return;
      if (type === 'image') imageMatched.add(el);
      if (type === 'link') linkMatched.add(el);
      deduped.push(el);
    });
    targetMap.set(selector, deduped);
  }

  for (const item of extracted) {
    const targets = targetMap.get(item.selector);
    const target = targets?.[item.index];

    if (!target) {
      mismatches++;
      continue;
    }

    if (item.type === 'text') {
      target.innerHTML = item.content;
    } else if (item.type === 'image') {
      target.setAttribute('src', item.content.src);
      if (item.content.alt) target.setAttribute('alt', item.content.alt);
      if (item.content.srcset) {
        target.setAttribute('srcset', item.content.srcset);
      }
      const picture = target.closest('picture');
      if (picture && item.content.src) {
        for (const source of picture.querySelectorAll('source')) {
          source.setAttribute('srcset', item.content.src);
        }
      }
    } else if (item.type === 'link') {
      target.setAttribute('href', item.content.href);
      if (item.content.text) target.textContent = item.content.text;
    }
  }

  return { html: root.innerHTML, mismatches };
}

/**
 * Assess structural risk of a variant swap.
 * Returns { risky, details } where risky is true if content would be lost.
 *
 * @param {string} oldHtml
 * @param {string} newHtml
 * @param {string} [category]
 */
export function assessSwapRisk(oldHtml, newHtml, category) {
  const parser = new DOMParser();
  const oldDoc = parser.parseFromString(`<div>${oldHtml}</div>`, 'text/html');
  const newDoc = parser.parseFromString(`<div>${newHtml}</div>`, 'text/html');
  const oldRoot = oldDoc.body.firstElementChild;
  const newRoot = newDoc.body.firstElementChild;
  const selectors = getSelectors(category);
  const losses = [];

  for (const { selector, type } of selectors) {
    if (type === 'text') continue; // text mismatches are low-risk
    const oldCount = oldRoot.querySelectorAll(selector).length;
    const newCount = newRoot.querySelectorAll(selector).length;
    if (oldCount > newCount && oldCount > 0) {
      losses.push(`${selector}: ${oldCount} → ${newCount}`);
    }
    if (oldCount > 0 && newCount === 0) {
      losses.push(`${selector}: present → absent`);
    }
  }

  // Deduplicate (a selector might appear in both conditions above)
  const unique = [...new Set(losses)];
  return {
    risky: unique.length > 0,
    details: unique.length > 0 ? unique.join(', ') : ''
  };
}
